/**
 * React Query hooks for Vertex AI connection data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getVertexSetupInstructions,
  listVertexConnections,
  createVertexConnection,
  deleteVertexConnection,
  syncVertexConnection,
} from "@/services/vertexService";
import type { VertexConnectionCreate } from "@/types/vertex";

const QUERY_KEY = "vertex-connections";

export function useVertexConnections(page: number = 1, itemsPerPage: number = 10) {
  return useQuery({
    queryKey: [QUERY_KEY, page, itemsPerPage],
    queryFn: () => listVertexConnections(page, itemsPerPage),
    staleTime: 30_000,
  });
}

export function useVertexSetupInstructions(enabled: boolean) {
  return useQuery({
    queryKey: ["vertex-setup-instructions"],
    queryFn: getVertexSetupInstructions,
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function useCreateVertexConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: VertexConnectionCreate) => createVertexConnection(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: "all" });
    },
  });
}

export function useDeleteVertexConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: number) => deleteVertexConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: "all" });
    },
  });
}

export function useSyncVertexConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (connectionId: number) => syncVertexConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY], refetchType: "all" });
    },
  });
}
